"use client";

import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import Link from "next/link";
import { Loader2, NotebookPen, Save, X } from "lucide-react";
import { api, type NoteListItem } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * 书本侧边笔记抽屉：内容页右下角的"笔记"入口。
 * 点击弹出右侧滑入抽屉：
 *  - 当前页笔记（自动保存，可编辑）
 *  - 本章笔记汇总（同章节其它笔记，可跳转）
 * 设计参考 Notion/Obsidian 侧边笔记：不离开当前页面，随时查看与记录。
 */
export default function NoteDrawer({
  pageId,
  conceptId,
  sectionId,
  chapterId,
  title,
  buttonClassName,
  buttonOffsetRight,
}: {
  pageId: string;
  conceptId?: string;
  sectionId?: string;
  chapterId?: string;
  title?: string;
  buttonClassName?: string;
  buttonOffsetRight?: number;
}) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  const [chapterNotes, setChapterNotes] = useState<NoteListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(true);
  const [saveError, setSaveError] = useState("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentRef = useRef("");
  const editVersionRef = useRef(0);
  const dirtyRef = useRef(false);
  const queuedVersionRef = useRef(-1);
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const loadRequestRef = useRef(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const activePageIdRef = useRef(pageId);
  const previousPageIdRef = useRef(pageId);
  const draftKey = `english-learning:note-draft:${pageId}`;

  useEffect(() => {
    activePageIdRef.current = pageId;
  }, [pageId]);

  const queueSave = useCallback((value: string, version: number) => {
    queuedVersionRef.current = Math.max(queuedVersionRef.current, version);
    setSaving(true);
    if (activePageIdRef.current === pageId) setSaveError("");
    const job = saveQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        await api.saveNote(pageId, value, {
          concept_id: conceptId,
          section_id: sectionId,
          chapter_id: chapterId,
          title,
        });
        const isActivePage = activePageIdRef.current === pageId;
        if (!isActivePage || version === editVersionRef.current) {
          try {
            localStorage.removeItem(draftKey);
          } catch {
            /* ignore */
          }
        }
        if (isActivePage && version === editVersionRef.current) {
          dirtyRef.current = false;
          setSaved(true);
          setSaveError("");
        }
      });
    saveQueueRef.current = job;
    void job
      .catch(() => {
        // 保留 dirty 状态和本地草稿；用户可明确重试。
        if (queuedVersionRef.current <= version) queuedVersionRef.current = version - 1;
        if (activePageIdRef.current === pageId && version === editVersionRef.current) {
          setSaveError("保存失败，草稿已保存在本机");
        }
      })
      .finally(() => {
        if (saveQueueRef.current === job) setSaving(false);
      });
    return job;
  }, [chapterId, conceptId, draftKey, pageId, sectionId, title]);

  const loadNote = useCallback(() => {
    const requestId = ++loadRequestRef.current;
    const startVersion = editVersionRef.current;
    const canHydrateEditor = !dirtyRef.current;
    setLoading(true);
    Promise.all([
      api.note(pageId),
      chapterId ? api.notes() : Promise.resolve([] as NoteListItem[]),
    ])
      .then(([note, all]) => {
        if (requestId !== loadRequestRef.current) return;
        if (
          canHydrateEditor &&
          !dirtyRef.current &&
          startVersion === editVersionRef.current
        ) {
          let localDraft: string | null = null;
          try {
            localDraft = localStorage.getItem(draftKey);
          } catch {
            /* ignore */
          }
          const nextContent = localDraft ?? note.content ?? "";
          contentRef.current = nextContent;
          setContent(nextContent);
          if (localDraft !== null) {
            editVersionRef.current += 1;
            dirtyRef.current = true;
            setSaved(false);
            setSaveError("已恢复本机草稿，尚未同步");
          } else {
            setSaved(true);
            setSaveError("");
          }
        }
        if (chapterId) {
          setChapterNotes(all.filter((n) => n.chapter_id === chapterId && n.page_id !== pageId));
        }
      })
      .catch(() => {
        // 加载失败时保留本地内容，避免覆盖仍在排队保存的编辑。
      })
      .finally(() => {
        if (requestId === loadRequestRef.current) setLoading(false);
      });
  }, [chapterId, draftKey, pageId]);

  function openDrawer() {
    setOpen(true);
    loadNote();
  }

  const closeDrawer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (dirtyRef.current) {
      void queueSave(contentRef.current, editVersionRef.current);
    }
    setOpen(false);
  }, [queueSave]);

  function handleChange(val: string) {
    contentRef.current = val;
    editVersionRef.current += 1;
    dirtyRef.current = true;
    try {
      localStorage.setItem(draftKey, val);
    } catch {
      /* beforeunload 仍会阻止用户无提示离开 */
    }
    setContent(val);
    setSaved(false);
    setSaveError("");
    if (timerRef.current) clearTimeout(timerRef.current);
    const version = editVersionRef.current;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void queueSave(val, version);
    }, 1200);
  }

  useEffect(() => {
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", warnBeforeUnload);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (dirtyRef.current && queuedVersionRef.current < editVersionRef.current) {
        void queueSave(contentRef.current, editVersionRef.current);
      }
    };
  }, [queueSave]);

  useEffect(() => {
    if (previousPageIdRef.current === pageId) return;
    previousPageIdRef.current = pageId;
    loadRequestRef.current += 1;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    contentRef.current = "";
    dirtyRef.current = false;
    setContent("");
    setChapterNotes([]);
    setSaved(true);
    setSaveError("");
    if (!open) return;
    const loadTimer = window.setTimeout(loadNote, 0);
    return () => window.clearTimeout(loadTimer);
  }, [loadNote, open, pageId]);

  useEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    closeButtonRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (event.key !== "Tab") return;

      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.tabIndex >= 0);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected) trigger.focus();
    };
  }, [closeDrawer, open]);

  return (
    <>
      {/* 触发按钮：右下角固定，书本侧边记东西的感觉 */}
      <button
        ref={triggerRef}
        onClick={openDrawer}
        className={cn(
          "fixed bottom-[calc(env(safe-area-inset-bottom)+4.75rem)] right-4 z-40 flex items-center gap-1.5 rounded-full border bg-background px-3 py-2 text-sm shadow-md transition-colors hover:text-primary hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:bottom-[4.5rem]",
          buttonOffsetRight !== undefined && "xl:right-[var(--note-button-right)]",
          buttonClassName
        )}
        style={
          buttonOffsetRight !== undefined
            ? ({ "--note-button-right": `${buttonOffsetRight}px` } as CSSProperties)
            : undefined
        }
        aria-label="打开笔记"
        aria-expanded={open}
        aria-controls="note-drawer"
        title="笔记"
      >
        <NotebookPen className="h-4 w-4" />
        <span className="hidden sm:inline">笔记</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20" onClick={closeDrawer} aria-hidden="true" />

          <div
            ref={dialogRef}
            id="note-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="note-drawer-title"
            tabIndex={-1}
            className="fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-md flex-col border-l bg-background shadow-2xl"
          >
        {/* 标题栏 */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div id="note-drawer-title" className="flex items-center gap-2 text-sm font-semibold">
            <NotebookPen className="h-4 w-4 text-primary" />
            笔记
            {title && <span className="text-xs font-normal text-muted-foreground">{title}</span>}
            <span role="status" aria-live="polite">
              {saving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-label="保存中" />}
              {saved && !saving && <Save className="h-3 w-3 text-success" aria-label="已保存" />}
            </span>
            {saveError && !saving && (
              <button
                type="button"
                onClick={() => void queueSave(contentRef.current, editVersionRef.current)}
                className="rounded px-1.5 py-0.5 text-[11px] font-medium text-destructive hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`${saveError}，点击重试保存`}
              >
                {saveError} · 重试
              </button>
            )}
          </div>
          <button
            ref={closeButtonRef}
            onClick={closeDrawer}
            className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="关闭笔记"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* 当前页笔记 */}
          <div className="mb-5">
            <div className="mb-2 text-xs font-semibold text-muted-foreground">本页笔记</div>
            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : (
              <textarea
                aria-label="本页笔记"
                value={content}
                onChange={(e) => handleChange(e.target.value)}
                placeholder="写下你对这个知识点的感悟..."
                rows={8}
                className="w-full resize-none rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            )}
          </div>

          {/* 本章汇总 */}
          {chapterId && (
            <div>
              <div className="mb-2 text-xs font-semibold text-muted-foreground">
                本章其它笔记（{chapterNotes.length}）
              </div>
              {chapterNotes.length === 0 ? (
                <div className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">
                  本章暂无其它笔记
                </div>
              ) : (
                <div className="space-y-2">
                  {chapterNotes.map((n) => (
                    <div key={n.id} className="rounded-md border bg-muted/20 p-2.5">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="truncate text-xs font-medium">
                          {n.concept_title || n.title || n.page_id}
                        </span>
                        {n.concept_id && (
                          <Link
                            href={`/concepts/${n.concept_id}`}
                            onClick={closeDrawer}
                            className="shrink-0 text-[11px] text-primary hover:underline"
                          >
                            跳转 →
                          </Link>
                        )}
                      </div>
                      <p className="line-clamp-3 whitespace-pre-wrap text-xs text-foreground/75">
                        {n.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
          </div>
        </>
      )}
    </>
  );
}
